/**
 * HoloScript Python/JS Interop Bindings
 *
 * Enables .hsplus modules to be used as drop-in libraries from Python and JS.
 * Generates binding wrappers that expose HoloScript modules as native imports.
 *
 * Usage:
 *   const bindings = new InteropBindingGenerator();
 *   bindings.generatePythonBindings(ast, 'output.py');
 *   bindings.generateJSBindings(ast, 'output.mjs');
 */

import type { HSPlusAST } from '../types/HoloScriptPlus';

export interface BindingExport {
  name: string;
  type: 'function' | 'class' | 'constant' | 'interface' | 'module';
  parameters?: BindingParameter[];
  returnType?: string;
  value?: unknown;
}

export interface BindingParameter {
  name: string;
  type: string;
  optional?: boolean;
  defaultValue?: string;
}

export interface GeneratedBinding {
  language: 'python' | 'javascript' | 'typescript';
  code: string;
  exports: string[];
  metadata: {
    sourceFile: string;
    generatedAt: string;
    holoScriptVersion: string;
  };
}

/**
 * InteropBindingGenerator — Generates Python/JS bindings from HoloScript ASTs
 */
export class InteropBindingGenerator {
  /**
   * Extract exports from a HoloScript AST
   */
  extractExports(ast: HSPlusAST): BindingExport[] {
    const exports: BindingExport[] = [];

    if (!ast.body) return exports;

    for (const node of ast.body) {
      const nodeRecord = node as unknown as Record<string, unknown>;
      const nodeType = nodeRecord.type as string | undefined;
      const nodeName = nodeRecord.name as string | undefined;

      if (nodeType === 'FunctionDeclaration' || nodeType === 'function') {
        const params = (nodeRecord.params || []) as Array<Record<string, unknown> | string>;
        exports.push({
          name: nodeName || 'anonymous',
          type: 'function',
          parameters: params.map((p) => {
            if (typeof p === 'string') {
              return { name: p, type: 'any', optional: false };
            }
            return {
              name: (p.name as string) || '',
              type: (p.type as string) || 'any',
              optional: (p.optional as boolean) || false,
            };
          }),
          returnType: (nodeRecord.returnType as string) || 'void',
        });
      }

      if (nodeType === 'template' || nodeType === 'ClassDeclaration') {
        exports.push({
          name: nodeName || 'UnnamedTemplate',
          type: 'class',
        });
      }

      if (nodeType === 'composition') {
        exports.push({
          name: nodeName || 'default',
          type: 'module',
        });
      }

      if (nodeType === 'VariableDeclaration' || nodeType === 'state') {
        exports.push({
          name: nodeName || 'unnamed',
          type: 'constant',
          value: nodeRecord.value,
        });
      }
    }

    return exports;
  }

  /**
   * Generate Python bindings from HoloScript AST
   */
  generatePythonBindings(ast: HSPlusAST, sourceFile: string): GeneratedBinding {
    const exports = this.extractExports(ast);
    const lines: string[] = [
      '"""',
      `Auto-generated Python bindings for ${sourceFile}`,
      `Generated by HoloScript InteropBindingGenerator`,
      `Do not edit manually — regenerate with: holoscript compile ${sourceFile} --target python`,
      '"""',
      '',
      'from typing import Any, Optional, Dict, List',
      'import json',
      '',
    ];

    for (const exp of exports) {
      switch (exp.type) {
        case 'function': {
          const params = (exp.parameters || [])
            .map((p) => {
              const pyType = this.tsPyType(p.type);
              return p.optional ? `${p.name}: ${pyType} = None` : `${p.name}: ${pyType}`;
            })
            .join(', ');
          const retType = this.tsPyType(exp.returnType || 'void');
          lines.push(`def ${exp.name}(${params}) -> ${retType}:`);
          lines.push(`    """HoloScript function: ${exp.name}"""`);
          lines.push(`    raise NotImplementedError("Bridge to HoloScript runtime required")`);
          lines.push('');
          break;
        }
        case 'class': {
          lines.push(`class ${exp.name}:`);
          lines.push(`    """HoloScript template: ${exp.name}"""`);
          lines.push(`    def __init__(self, **kwargs: Any) -> None:`);
          lines.push(`        self._props = kwargs`);
          lines.push('');
          break;
        }
        case 'constant': {
          lines.push(`${exp.name} = ${JSON.stringify(exp.value ?? 'None')}`);
          lines.push('');
          break;
        }
        case 'module': {
          lines.push(`class ${exp.name}Module:`);
          lines.push(`    """HoloScript composition: ${exp.name}"""`);
          lines.push(`    pass`);
          lines.push('');
          break;
        }
      }
    }

    // Add __all__
    lines.push(`__all__ = [${exports.map((e) => `"${e.name}"`).join(', ')}]`);

    return {
      language: 'python',
      code: lines.join('\n'),
      exports: exports.map((e) => e.name),
      metadata: {
        sourceFile,
        generatedAt: new Date().toISOString(),
        holoScriptVersion: '5.0.0',
      },
    };
  }

  /**
   * Generate JavaScript (ESM) bindings from HoloScript AST
   */
  generateJSBindings(ast: HSPlusAST, sourceFile: string): GeneratedBinding {
    const exports = this.extractExports(ast);
    const lines: string[] = [
      `// Auto-generated JS bindings for ${sourceFile}`,
      `// Generated by HoloScript InteropBindingGenerator`,
      `// Do not edit manually`,
      '',
    ];

    for (const exp of exports) {
      switch (exp.type) {
        case 'function': {
          const params = (exp.parameters || []).map((p) => p.name).join(', ');
          lines.push(`export function ${exp.name}(${params}) {`);
          lines.push(`  throw new Error("Bridge to HoloScript runtime required");`);
          lines.push(`}`);
          lines.push('');
          break;
        }
        case 'class': {
          lines.push(`export class ${exp.name} {`);
          lines.push(`  constructor(props = {}) { this._props = props; }`);
          lines.push(`}`);
          lines.push('');
          break;
        }
        case 'constant': {
          lines.push(`export const ${exp.name} = ${JSON.stringify(exp.value ?? null)};`);
          lines.push('');
          break;
        }
        case 'module': {
          lines.push(`export const ${exp.name} = { /* HoloScript composition */ };`);
          lines.push('');
          break;
        }
      }
    }

    return {
      language: 'javascript',
      code: lines.join('\n'),
      exports: exports.map((e) => e.name),
      metadata: {
        sourceFile,
        generatedAt: new Date().toISOString(),
        holoScriptVersion: '5.0.0',
      },
    };
  }

  /**
   * Map TypeScript types to Python types
   */
  private tsPyType(tsType: string): string {
    const mapping: Record<string, string> = {
      string: 'str',
      number: 'float',
      int: 'int',
      boolean: 'bool',
      void: 'None',
      any: 'Any',
      unknown: 'Any',
      'string[]': 'List[str]',
      'number[]': 'List[float]',
    };
    return mapping[tsType] || 'Any';
  }
}
